import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import runtime from '@/runtime';
import exportLeads from '@/utils/exports/exportLeads';
import * as notifications from '@/utils/notifications';

const orgName = runtime.organization?.name ?? 'autoRetain';

/**
 * Page that automatically downloads leads via a shared secret link.
 * Extracts the `secret` query parameter from the URL, triggers the export,
 * and redirects to the home page afterward.
 */
const DownloadLeadsPage = () => {
    const { t } = useTranslation('leads');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const secret = searchParams.get('secret');
    const hasTriggered = useRef(false);

    useEffect(() => {
        if (hasTriggered.current) {
            return;
        }

        hasTriggered.current = true;

        if (!secret) {
            void navigate('/', { replace: true });

            return;
        }

        const download = async () => {
            try {
                await exportLeads(t, undefined, secret);
                notifications.success({ message: t('leads:download.succeed') });
            } catch {
                notifications.error({ message: t('leads:download.failed') });
            } finally {
                void navigate('/', { replace: true });
            }
        };

        void download();
    }, [secret, t, navigate]);

    return (
        <Helmet>
            <title>{`Download Leads — ${orgName}`}</title>
            <meta content={`Download Leads — ${orgName}`} property="og:title" />
            <meta content={`Lead export from ${orgName}`} property="og:description" />
            <meta content="website" property="og:type" />
        </Helmet>
    );
};

export default DownloadLeadsPage;
